import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { ShieldCheck, Loader2, CreditCard, ArrowLeft, CheckCircle2, AlertCircle } from "lucide-react";
import { COMPANY } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cardElementOptions = {
  style: {
    base: { color: "#fff", fontSize: "15px", "::placeholder": { color: "rgba(255,255,255,0.35)" } },
    invalid: { color: "#f87171" },
  },
};

const Row = ({ label, value }) => (
  <div className="flex justify-between"><span className="text-white/45">{label}</span><span className="text-white/85">{value}</span></div>
);

function Inner({ token, info }) {
  const stripe = useStripe();
  const elements = useElements();
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(null);

  const submit = async () => {
    if (!stripe || !elements || !consent) return;
    setBusy(true);
    setError("");
    try {
      const card = elements.getElement(CardElement);
      const { setupIntent, error: confirmError } = await stripe.confirmCardSetup(info.client_secret, {
        payment_method: { card },
      });
      if (confirmError) {
        setError(confirmError.message || "Your card could not be verified.");
        setBusy(false);
        return;
      }
      if (setupIntent.status !== "succeeded") {
        setError(`Setup status: ${setupIntent.status}. Please try again.`);
        setBusy(false);
        return;
      }
      const { data } = await axios.post(`${API}/card-setup/${token}/confirm`, { setup_intent_id: setupIntent.id });
      setSaved(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not save your card. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (saved) {
    return (
      <div className="text-center py-6" data-testid="card-setup-success">
        <CheckCircle2 className="text-emerald-400 mx-auto" size={44} />
        <h2 className="font-display font-black text-2xl mt-3">Card Saved</h2>
        <p className="text-white/60 text-sm mt-2">
          {saved.brand ? saved.brand[0].toUpperCase() + saved.brand.slice(1) : "Card"} &bull;&bull;&bull;&bull; {saved.last4} is now on file.
        </p>
        <p className="text-white/40 text-xs mt-3">No payment was collected. Thank you!</p>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-lg border border-white/15 bg-[#0A0A0A] p-3.5 mt-4">
        <CardElement options={cardElementOptions} onChange={() => setError("")} />
      </div>
      {error && <p data-testid="card-setup-error" className="text-red-400 text-xs mt-2">{error}</p>}
      <label className="flex items-start gap-2.5 mt-4 text-xs text-white/65 cursor-pointer select-none">
        <input type="checkbox" data-testid="card-setup-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 h-4 w-4 accent-[#F2B705] shrink-0" />
        <span>
          I authorize AutoHire STL to securely save this card with Stripe for use only as authorized by my signed rental
          agreement (for example, tolls, citations, or damage beyond normal wear). I understand no payment is being
          collected right now, and saving a card does not guarantee funds will be available for any future authorized charge.
        </span>
      </label>
      <button data-testid="card-setup-submit" onClick={submit} disabled={busy || !consent || !stripe}
        className="mt-5 w-full bg-[#F2B705] text-black font-bold py-3.5 rounded-md hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
        {busy ? <Loader2 className="animate-spin" size={18} /> : <CreditCard size={18} />} Save Card Securely
      </button>
    </>
  );
}

export default function CardSetup() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const stripePromise = useMemo(() => loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY), []);

  useEffect(() => {
    axios.get(`${API}/card-setup/${token}`)
      .then(({ data }) => setInfo(data))
      .catch((e) => setError(e.response?.data?.detail || "This link is invalid."))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center px-5 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-[#F2B705] text-sm mb-6 transition-colors">
          <ArrowLeft size={16} /> Back to site
        </Link>
        <div className="flex items-center gap-3">
          <img src={COMPANY.logo} alt="Auto-Hire STL" className="h-14 w-14 object-contain rounded-md" />
          <div className="font-display font-extrabold text-2xl">AutoHire <span className="text-gold-gradient">STL</span></div>
        </div>

        <div className="mt-8 bg-[#0C0C0C] border border-white/10 rounded-2xl p-7" data-testid="card-setup-page">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#F2B705]" /></div>
          ) : error ? (
            <div className="text-center py-6" data-testid="card-setup-page-error">
              <AlertCircle className="text-red-400 mx-auto" size={40} />
              <p className="text-white/70 mt-3">{error}</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-[#F2B705] text-sm font-bold uppercase tracking-widest">
                <ShieldCheck size={16} /> Secure Card on File
              </div>
              <h1 className="font-display font-black text-3xl mt-2">Save Your Card</h1>
              <div className="mt-5 space-y-2 text-sm">
                <Row label="Name" value={info.name} />
                <Row label="Vehicle" value={info.vehicle} />
                <Row label="Rental" value={`${info.pickup_date} \u2192 ${info.return_date}`} />
              </div>
              <div className="mt-5 rounded-lg border border-sky-400/30 bg-sky-400/[0.06] p-3.5 text-xs text-sky-100/90 space-y-1.5">
                <p><b className="text-white">No rental payment, deposit, or hold is being collected here.</b> Insurance requires every renter &mdash; including cash rentals &mdash; to have a card on file.</p>
                <p>Your bank may briefly show a standard card-verification entry; that's a routine check by your card issuer, not a charge from us, and it clears automatically.</p>
                <p>Future charges, if any, are only ever made as authorized by your signed rental agreement &mdash; saving a card is never a guarantee against it.</p>
              </div>
              <Elements stripe={stripePromise}>
                <Inner token={token} info={info} />
              </Elements>
              <p className="text-white/35 text-xs text-center mt-4">Powered by Stripe. Your card details are sent directly to Stripe and never stored on our servers.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
